import { API_URL, PING_URL, APP_VERSION } from "../config/appConfig";
import { getDeviceId, DB } from "../lib/deviceStorage";
import { stableJson } from "../lib/syncUtils";
import { recordApiCall } from "./apiTrace"; // v3.2.08

const API_RATE_LIMIT_BACKOFF_KEY = "fz:api:backoffUntil";
let apiBackoffUntilMemory = 0;
let connectivityCheckCache = { at: 0, result: null, promise: null };
const CONNECTIVITY_CHECK_CACHE_MS = 30000;
const POST_UPDATE_HANDOFF_KEY = `fz:postUpdateHandoffUntil:${APP_VERSION}`;
// v2.242.02: limpar backoff de API de versões anteriores.
// O backoff de 429 persiste em localStorage e sobrevive a "limpar cache" no browser.
// Quando o app atualiza de versão, o contexto mudou e o backoff antigo deve ser descartado.
const LAST_BACKOFF_APP_VERSION_KEY = "fz:api:backoffAppVersion";
try {
  const lastBackoffVersion = localStorage.getItem(LAST_BACKOFF_APP_VERSION_KEY) || "";
  if (lastBackoffVersion !== String(APP_VERSION || "")) {
    localStorage.removeItem(API_RATE_LIMIT_BACKOFF_KEY);
    apiBackoffUntilMemory = 0;
    localStorage.setItem(LAST_BACKOFF_APP_VERSION_KEY, String(APP_VERSION || ""));
  }
} catch (_) {}

function getPostUpdateHandoffDelayMs() {
  try {
    const fromWindow = Number(typeof window !== "undefined" ? window.__fzPostUpdateHandoffUntil || 0 : 0) || 0;
    const fromSession = Number(typeof sessionStorage !== "undefined" ? sessionStorage.getItem(POST_UPDATE_HANDOFF_KEY) || 0 : 0) || 0;
    return Math.max(0, Math.max(fromWindow, fromSession) - Date.now());
  } catch (e) {
    return 0;
  }
}

async function awaitPostUpdateHandoff(maxWaitMs = 4200) {
  const delay = Math.min(Math.max(0, Number(maxWaitMs) || 0), getPostUpdateHandoffDelayMs());
  if (delay > 0) await new Promise(resolve => setTimeout(resolve, delay));
}

function createIdempotencyKey(farmId, collection, baseVersion, payload) {
  const raw = `${farmId}|${collection}|${baseVersion}|${stableJson(payload)}`;
  let hash = 0;
  for (let i = 0; i < raw.length; i += 1) {
    hash = ((hash << 5) - hash) + raw.charCodeAt(i);
    hash |= 0;
  }
  return `idem_${Math.abs(hash)}_${raw.length}`;
}

function nowMs() { return Date.now(); }

export function getApiBackoffUntil() {
  try { return Math.max(apiBackoffUntilMemory || 0, Number(localStorage.getItem(API_RATE_LIMIT_BACKOFF_KEY) || 0)); }
  catch (e) { return apiBackoffUntilMemory || 0; }
}

export function setApiBackoff(ms = 120000) {
  const until = nowMs() + Math.max(15000, Number(ms) || 120000);
  apiBackoffUntilMemory = Math.max(apiBackoffUntilMemory || 0, until);
  try { localStorage.setItem(API_RATE_LIMIT_BACKOFF_KEY, String(apiBackoffUntilMemory)); } catch (e) {}
}

function isAppApiRequest(input) {
  const url = typeof input === "string" ? input : String(input?.url || "");
  return !!url && (url.includes("/api/") || url === API_URL || url.includes(API_URL));
}

let sessionAbortController = typeof AbortController !== "undefined" ? new AbortController() : null;

function getSessionSignal() {
  if (!sessionAbortController && typeof AbortController !== "undefined") {
    sessionAbortController = new AbortController();
  }
  return sessionAbortController?.signal || null;
}

function abortSessionRequests() {
  try { sessionAbortController?.abort?.(); } catch (_) {}
  sessionAbortController = typeof AbortController !== "undefined" ? new AbortController() : null;
}

function combineAbortSignals(...signals) {
  const validSignals = signals.filter(Boolean);
  if (!validSignals.length || typeof AbortController === "undefined") return validSignals[0] || undefined;
  const controller = new AbortController();
  const abort = () => { try { controller.abort(); } catch (_) {} };
  for (const signal of validSignals) {
    if (signal?.aborted) { abort(); break; }
    try { signal.addEventListener("abort", abort, { once: true }); } catch (_) {}
  }
  return controller.signal;
}

function getTimeoutSignal(ms) {
  if (typeof AbortSignal !== "undefined" && typeof AbortSignal.timeout === "function") {
    return { signal: AbortSignal.timeout(ms), clear(){} };
  }
  const controller = typeof AbortController !== "undefined" ? new AbortController() : null;
  const timer = controller ? setTimeout(() => controller.abort(), ms) : null;
  return { signal: controller?.signal, clear(){ if (timer) clearTimeout(timer); } };
}

async function fetchWithTimeout(input, init = {}, timeoutMs = 8000) {
  const isApi = isAppApiRequest(input);
  const url = typeof input === "string" ? input : String(input?.url || "");
  let traceAction = "";
  let traceCollection = ""; // v3.2.11: capturar collection/key para diagnóstico
  if (isApi) {
    try {
      if (typeof init?.body === "string") {
        const parsed = JSON.parse(init.body);
        traceAction = String(parsed?.action || "");
        traceCollection = String(parsed?.collection || parsed?.key || "").slice(0, 80);
      }
    } catch (_) {}
  }
  const traceStart = Date.now();

  if (isApi) {
    await awaitPostUpdateHandoff(
      Math.min(4200, Math.max(0, (Number(timeoutMs) || 8000) - 1200))
    );
    const backoffUntil = getApiBackoffUntil();
    if (backoffUntil > nowMs()) {
      // v3.2.08: registrar local_backoff
      recordApiCall({ url, action: traceAction, collection: traceCollection, source: "apiClient",
        status: 429, durMs: Date.now() - traceStart, result: "local_backoff" });
      const err = new Error("api-rate-limited-backoff");
      err.status = 429;
      err.backoffUntil = backoffUntil;
      err.__fzTraced = true;
      throw err;
    }
  }

  const timeout = getTimeoutSignal(timeoutMs);
  const combinedSignal = combineAbortSignals(init.signal, timeout?.signal, isApi ? getSessionSignal() : null);
  try {
    const response = await fetch(input, { ...init, signal: combinedSignal || timeout?.signal || init.signal });
    if (isApi && (response?.status === 429 || response?.status === 503)) {
      setApiBackoff(response.status === 429 ? 180000 : 90000);
    }
    if (isApi) {
      try {
        recordApiCall({ url, action: traceAction, collection: traceCollection, source: "apiClient",
          status: response.status, durMs: Date.now() - traceStart,
          result: response.status === 429 ? "real_429"
            : response.status === 503 ? "real_503"
            : (response.ok ? "ok" : "error") });
      } catch (_) {}
    }
    return response;
  } catch (e) {
    if (isApi && !e?.__fzTraced) {
      try {
        const msg = String(e?.message || "");
        recordApiCall({ url, action: traceAction, collection: traceCollection, source: "apiClient",
          status: e?.status || 0, durMs: Date.now() - traceStart,
          result: (msg.includes("timeout") || e?.name === "TimeoutError")
            ? "timeout" : e?.name === "AbortError" ? "abort" : "network_error" });
      } catch (_) {}
    }
    throw e;
  } finally {
    try { timeout?.clear?.(); } catch (e) {}
  }
}

async function hasRealConnectivity() {
  if (!navigator.onLine) return false;

  const now = nowMs();
  if (connectivityCheckCache.promise) {
    try { return await connectivityCheckCache.promise; } catch (e) { return !!navigator.onLine; }
  }
  if (connectivityCheckCache.result !== null && (now - connectivityCheckCache.at) < CONNECTIVITY_CHECK_CACHE_MS) {
    return connectivityCheckCache.result;
  }

  const runCheck = (async () => {
    const candidates = [];
    const seen = new Set();
    const addCandidate = (url) => {
      const normalized = String(url || "");
      if (!normalized || seen.has(normalized)) return;
      seen.add(normalized);
      candidates.push(normalized);
    };
    const minuteStamp = Math.floor(Date.now() / 60000);
    try { addCandidate(`${PING_URL}?t=${minuteStamp}`); } catch (e) {}
    try {
      const apiUrl = new URL(API_URL, window.location.origin);
      apiUrl.pathname = apiUrl.pathname.replace(/\/[^/]*$/, '/ping.php');
      apiUrl.searchParams.set('t', String(minuteStamp));
      addCandidate(apiUrl.toString());
    } catch (e) {}

    for (const url of candidates) {
      try {
        const resp = await fetchWithTimeout(url, { method: "GET", cache: "no-cache", headers: { "X-Device-ID": getDeviceId() } }, 5000);
        if (resp?.ok) return true;
      } catch (e) {}
    }

    return !!navigator.onLine;
  })();

  connectivityCheckCache.promise = runCheck;
  try {
    const result = await runCheck;
    connectivityCheckCache = { at: nowMs(), result: !!result, promise: null };
    return !!result;
  } catch (e) {
    connectivityCheckCache = { at: nowMs(), result: !!navigator.onLine, promise: null };
    return !!navigator.onLine;
  }
}

async function logSyncTelemetry(entry) {
  try {
    const current = (await DB.get("fz:sync:telemetry")) || [];
    const next = [{ id: `sync_${Date.now()}_${Math.random().toString(36).slice(2,8)}`, at: new Date().toISOString(), ...entry }, ...current].slice(0, 50);
    await DB.set("fz:sync:telemetry", next);
    await DB.set("fz:sync:last_status", next[0]);
  } catch (e) {}
}

export { createIdempotencyKey, getTimeoutSignal, getSessionSignal, abortSessionRequests, getPostUpdateHandoffDelayMs, awaitPostUpdateHandoff, fetchWithTimeout, hasRealConnectivity, logSyncTelemetry };
